import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ServiceInfoPrompt, type ServiceInfoData } from './ServiceInfoPrompt';
import { Play, Pause, Square } from "lucide-react";
import { toast } from "sonner";
import {
  readMyDiagnosticCaptureCandidates,
  type DiagnosticCaptureAssertion,
} from "@/utils/ticketTimerManager";
// STAGE A — the timer interaction foundation: work state, plan, executor,
// outcome model, success-only effects, single-flight interaction.
import {
  deriveTimerWorkState,
  availableTimerActions,
  type TimerActionIntent,
} from "@/lib/timer/interaction/workState";
import { buildTimerActionPlan, type TimerInputStep } from "@/lib/timer/interaction/actionPlan";
import {
  executeTimerAction,
  runTimerSuccessEffects,
  type TimerActionRequest,
  type TimerSuccessEffectHandlers,
  type TimerWorkTarget,
} from "@/lib/timer/interaction/executor";
import type { TimerActionResult, TimerActionSuccess } from "@/lib/timer/interaction/outcomes";
import { TimerInteraction } from "@/lib/timer/interaction/actionLifecycle";
import { readFacilityLayout, readGuidedStartContext } from "@/lib/location/api";
import { assistAnswer, readLocationAssist, NO_LOCATION_ASSIST } from "@/lib/location/assist";
import { acceptTrivialTimerReview, trivialReviewNotice, type TrivialReviewResult } from "@/utils/timerEpisodeReview";
import type { FacilityLayout } from "@/lib/location/facilityLayout";
import {
  decideGuidedStart,
  guidedBayChoices,
  resumeBayChoiceDecision,
  type GuidedBayChoice,
  type GuidedStartContext,
  type GuidedStartDecision,
} from "@/lib/timer/interaction/guidedStart";
import { GuidedStartDialog, type GuidedStartMode } from "./GuidedStartDialog";
import {
  readMyTimerControlState,
  readMyFinishWorkContext,
  type TicketTimerProjection,
  type MyTimerControlState,
} from "@/utils/ticketWorkLifecycle";
import { describeEodConflict, describeUnconfirmed, loadServerEodVerdict, markEodPendingVerification, resumeEodAction } from '@/lib/scheduling/serverEodState';
import { isPartOfRolloverChain } from '@/utils/rolloverChainMembership';
import { supabase } from "@/integrations/supabase/client";
import { useMarketingMode } from "@/contexts/MarketingModeContext";
import { PreAuthRequiredModal } from "@/components/invoicing/PreAuthRequiredModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PauseWorkDialog } from './PauseWorkDialog';
import { StopWorkDialog } from './StopWorkDialog';
import type { DispositionJob } from '@/utils/ticketWorkLifecycle';
import {
  draftToDestination,
  finishLocationWords,
  offersEntireRepairChoice,
  sessionConclusionJobs,
  stopConfirmState,
  stopDialogMode,
  stopEndsRepair,
  stopJobDispositions,
  stopWithholdsCompletion,
  type DestinationDraft,
  type FinishWorkContext,
  type JobDisposition,
  type StopDialogMode,
  type StopScope,
} from '@/lib/timer/interaction/simplifiedStop';
import { readParkingSpotChoices, type ParkingSpotChoice } from '@/lib/location/api';
import {
  normalizePauseNote,
  pauseAttentionMessage,
  pauseOption,
  pauseRequestsFohCustody,
  type PauseDetail,
} from '@/lib/timer/interaction/pauseIntent';
import { TimerAllocationReviewDialog } from './TimerAllocationReviewDialog';
import { openTimerReviewOutsideCard } from '@/lib/timer/interaction/reviewHost';
import { DiagnosticPauseCapture } from './DiagnosticPauseCapture';
// oct20a S1: voluntary findings beside my live timer; what my session found at a non-final Stop.
import { DiagnosticEvidenceButton } from './DiagnosticEvidence';
import { SessionConclusionFields } from './SessionConclusionFields';
import {
  sessionConclusionComplete, sessionConclusionPayload, type SessionConclusionDraft,
} from '@/lib/diagnostics/evidence';
import type { DiagnosticCaptureData, UncapturedDiagnosticJob } from '@/types/diagnostic';
// FOH handoff. Pause (Stage D): the advisor answer's handoff routes through
// `useTicketCustodyMutation` (cache invalidation, OCIS emit, advisor-action
// emit). Stop (Stage E): the SERVER hands off inside finish_my_work; the
// client only runs the same follow-up (`useCustodySuccessEffects`).
import {
  useTicketCustodyMutation,
  useCustodySuccessEffects,
  useTicketCustody,
  nextCustodyEventOnTimerStart,
} from '@/hooks/tickets/useTicketCustody';
import {
  FINAL_STOP_HANDOFF_REASON,
  buildCustodyHandoffMetadata,
  getCustodyHandoffReasonLabel,
} from '@/lib/tickets/lifecycle/custodyHandoffReasons';
import {
  decidePauseFohHandoff,
  type PauseBoundaryResult,
} from '@/lib/tickets/lifecycle/stopFohHandoffDecision';
// DTC-J: route the EOD-resume pause-duration anchor through the
// canonical clock so the resume-path business decision feeding
// `checkEODViolation(...)` is deterministic under
// `clock.setFixed(...)`. Same drift class as DTC-F V4 (useEODMonitor).
import { clock } from '@/utils/clock';
import { offerWorkSwitchAfterRefusal } from '@/lib/timer/workSwitch';

/** The human facts a Start collects (scope, then — for a parked vehicle — the target). */
type TimerActFacts = {
  jobIds: string[];
  target: TimerWorkTarget | null;
  /** Stage C — bays the server refused during THIS act; never proposed again. */
  refusedBayIds: string[];
};

/** Stage C — the Guided Start dialog's state (closed when `decision` is null). */
type StopCamera = { foundSpotId: string | null; suggestedSpotId: string | null };
const NO_STOP_CAMERA: StopCamera = { foundSpotId: null, suggestedSpotId: null };

type GuidedStartUi = {
  mode: GuidedStartMode;
  decision: Extract<GuidedStartDecision, { kind: 'MOVE_PLANNED' | 'CHOOSE_BAY' | 'BAY_MISMATCH' | 'CAMERA_FOUND' }>;
  choices: GuidedBayChoice[];
  vehicleLabel: string | null;
  /** The shop floor for the map picker (item #14); null → the List. */
  layout: FacilityLayout | null;
  /** Oct 20 (D-4): the act the bay choice belongs to (Resume of parked paused work, or Start). */
  act?: 'start' | 'resume';
} | null;

/** One open job line a technician may put inside a Start scope. */
interface StartableJob {
  jobId: string;
  name: string;
  /** `ticket_jobs.minutes_override ?? jobs.standard_minutes` — Core's authority. */
  expectedMinutes: number | null;
}

interface TicketTimerControlsProps {
  ticketId: string;
  /**
   * PTP-012 — PRESENTATION / LOGGING ONLY. NOT an identity authority.
   *
   * Every timer authority on the server resolves WHO from `my_technician_id()`,
   * i.e. the authenticated caller — `start_my_timer_with_scope`,
   * `pause_my_timer`, `resume_my_timer` and `stop_my_timer_for_review` all
   * ignore whatever is passed here. The card used to supply
   * `ticket.assignedTechId` and could guarantee it was non-null only because
   * the surrounding JSX refused to render unless a technician was assigned.
   * That gate is gone (assignment was never a Start prerequisite, and
   * `tickets_parking_lot_immunity` forces the column NULL on parked tickets),
   * so this prop is now legitimately absent.
   *
   * It must never be defaulted to `''` to satisfy the type: `checkEODViolation`
   * looks a technician up by this id and returns `{ exceeds: false }` when the
   * row is not found, so an empty string would silently switch the end-of-day
   * guard OFF rather than fail. The EOD path below reads the AUTHENTICATED
   * viewer's id instead.
   */
  techId?: string | null;
  /**
   * PTP-012 — the vehicle is PARKED and the canonical pull-in applies.
   *
   * A parked vehicle cannot hold a running timer
   * (`tickets_parking_lot_no_running_timer`), and a technician may not relocate
   * one on its own (`can_relocate_vehicles_for_shop` excludes TECHNICIAN by
   * design). So Start here is not the ordinary Start: it asks which bay the car
   * is going into and performs ONE atomic server act,
   * `pull_in_and_start_my_timer`, which moves and starts together or does
   * neither.
   */
  requiresPullIn?: boolean;
  shopId: string;
  timerStatus: string;
  onTimerUpdate: () => void;
  customerName?: string;
  hasFilmModeOverride?: boolean;
  /**
   * STAGE 0B — may this viewer make the end-of-day scheduling decision
   * (roll over / resume anyway)? The card passes `canAssignTickets`, the same
   * placement authority that gates the `eod-exceeded` listener in
   * `EODRolloverContainer`. When this is not `true`, an EOD-blocked Resume
   * tells the technician why instead of silently doing nothing.
   */
  canDecideEndOfDay?: boolean;
  /**
   * STAGE 0C — does this viewer hold the manager work-override authority for
   * pre-repair authorization (OWNER/MANAGER, the server's rule)? When not
   * `true`, a refused Start/Resume is explained with no commercial controls.
   */
  canOverridePreauth?: boolean;
  /**
   * The ticket is operationally COMPLETED (a final Stop). Offer nothing but
   * this technician's still-open time review, if there is one — a completed
   * ticket takes no Start, Pause, Resume or Stop.
   */
  reviewOnly?: boolean;
}

export function TicketTimerControls({
  ticketId,
  techId,
  requiresPullIn = false,
  shopId,
  timerStatus,
  onTimerUpdate,
  customerName,
  hasFilmModeOverride = false,
  canDecideEndOfDay,
  canOverridePreauth,
  reviewOnly = false,
}: TicketTimerControlsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
  // Stage C — Guided Start (replaces the PTP-012 bay dropdown).
  const [guided, setGuided] = useState<GuidedStartUi>(null);
  // #19 — the camera's answer / suggestion for the Stop's parking question.
  const [stopCamera, setStopCamera] = useState<StopCamera>(NO_STOP_CAMERA);
  const [isPullingIn, setIsPullingIn] = useState(false);
  const [showServiceInfoPrompt, setShowServiceInfoPrompt] = useState(false);

  // ── TIMER V2 8B: CONTROLS BELONG TO *THIS* TECHNICIAN ────────────────────
  // `timerStatus` (the prop) is the TICKET's projection. It still drives
  // ticket-level display, but it cannot decide which buttons this technician
  // gets: on a ticket two people share, a technician whose own work is paused
  // was being shown Pause + Stop because a COLLEAGUE was running, and a
  // technician who had never touched the ticket was shown the same.
  //
  // `UNKNOWN` is the honest initial value. It is deliberately NOT `IDLE`,
  // because offering Start to someone who may already be running is exactly
  // the mistake this replaces.
  const [myState, setMyState] = useState<MyTimerControlState>({
    myState: 'UNKNOWN',
    shopId: null,
    technicianId: null,
    workEpisodeId: null,
    sessionId: null,
    ticketTimerStatus: null,
    pendingReviewCount: 0,
  });

  // ── START SCOPE SELECTION ────────────────────────────────────────────────
  // The job set a technician is about to assert they are working on.
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [startableJobs, setStartableJobs] = useState<StartableJob[]>([]);
  // A rollover continuation works its repair's jobs, which live on the chain's
  // first ticket: set to that ticket's number while choosing a scope here.
  const [continuedFrom, setContinuedFrom] = useState<string | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
  // Stage D — the ONE Pause answer ("Why are you stopping work?") and its optional note.
  const [pauseDetail, setPauseDetail] = useState<PauseDetail | ''>('');
  const [pauseNote, setPauseNote] = useState('');

  // ── 8E: ALLOCATION REVIEW ────────────────────────────────────────────────
  // The episode whose frozen proposal the review dialog is showing. Set from a
  // Stop result to open it immediately, and from `myState.workEpisodeId` when
  // the technician reopens it later.
  //
  // Opening is a UX shortcut ONLY. Nothing about acceptance depends on the
  // dialog having been opened by the Stop that created the episode — a
  // technician who closes it, navigates away, refreshes, or comes back on
  // another machine reaches the same review through the same RPC.
  const [reviewEpisodeId, setReviewEpisodeId] = useState<string | null>(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const openReview = useCallback((episodeId: string | null | undefined) => {
    if (!episodeId) return;
    setReviewEpisodeId(episodeId);
    setIsReviewOpen(true);
  }, []);
  const { isMarketingMode, notifyTimerStarted } = useMarketingMode();
  
  // Diagnostic pause-lock state - now supports multiple jobs
  const [uncapturedDiagJobs, setUncapturedDiagJobs] = useState<UncapturedDiagnosticJob[]>([]);
  const [capturedJobs, setCapturedJobs] = useState<DiagnosticCaptureData[]>([]);
  const [skippedJobIds, setSkippedJobIds] = useState<Set<string>>(new Set());
  // Pending data from forms that are filled out but not yet submitted (for auto-capture on pause)
  const [pendingDiagData, setPendingDiagData] = useState<DiagnosticCaptureData[]>([]);
  
  // Diagnostic stop-lock state (separate from pause to allow independent flows)
  const [uncapturedDiagJobsForStop, setUncapturedDiagJobsForStop] = useState<UncapturedDiagnosticJob[]>([]);
  const [capturedJobsForStop, setCapturedJobsForStop] = useState<DiagnosticCaptureData[]>([]);
  const [skippedJobIdsForStop, setSkippedJobIdsForStop] = useState<Set<string>>(new Set());
  // Fully answered but not yet confirmed findings (same rule as Pause).
  const [pendingDiagDataForStop, setPendingDiagDataForStop] = useState<DiagnosticCaptureData[]>([]);

  // Pause's advisor handoff (Stage D) is fired AFTER the timer mutation
  // succeeds; failure isolation: a custody failure never rolls back the
  // timer success (the hook surfaces its own destructive toast).
  const custodyMutation = useTicketCustodyMutation();
  // Stage E: the follow-up of a handoff the SERVER performed inside
  // finish_my_work (cache, OCIS timeline, advisor action) — never a second
  // transition, and no "Also kick to FOH?" question on Stop.
  const custodyFollowUp = useCustodySuccessEffects();
  // Stage E — Simplified Stop: the facts the dialog shows, and the one
  // answer it may need ("Where are you leaving the vehicle?").
  const [finishCtx, setFinishCtx] = useState<FinishWorkContext | null>(null);
  const [finishCtxLoaded, setFinishCtxLoaded] = useState(false);
  // oct20a S1: session conclusions (a Stop that ends my diagnostic session but not the diagnosis).
  const [sessionConclusions, setSessionConclusions] = useState<Record<string, SessionConclusionDraft>>({});
  const [serverConclusionJobs, setServerConclusionJobs] = useState<Array<{ jobId: string; name: string }>>([]);
  const [spotChoices, setSpotChoices] = useState<ParkingSpotChoice[]>([]);
  const [stopDestination, setStopDestination] = useState<DestinationDraft>(null);
  const [forceLocationQuestion, setForceLocationQuestion] = useState(false);
  const [refusedSpotIds, setRefusedSpotIds] = useState<Set<string>>(new Set());
  // Final Stop: jobs no frozen scope points at, and the technician's answer
  // for each. The server may name more than the advisory context did.
  const [serverUnresolvedJobs, setServerUnresolvedJobs] = useState<DispositionJob[]>([]);
  const [jobDispositions, setJobDispositions] = useState<Record<string, JobDisposition>>({});
  // oct20 FD-02: jobs the final technician says are NOT finished (left open;
  // the ticket then goes to the front office without being completed).
  const [unfinishedJobIds, setUnfinishedJobIds] = useState<Set<string>>(new Set());
  /** OCT28 #14: "Today's Work Complete" / "Entire Repair Complete" — never preselected. */
  const [stopScope, setStopScope] = useState<StopScope | null>(null);

  // Read current custody for the post-timer auto-accept decision. When
  // the ticket is in `pending_tech_accept` (kicked back from FOH) and
  // the technician starts/resumes the timer, we auto-fire `boh_accept`
  // so the FOH queue stops including the ticket and the BOH pending
  // pill clears. See nextCustodyEventOnTimerStart for the pure rule.
  const { data: custodyRow } = useTicketCustody(ticketId);

  // Fire-and-forget post-timer custody acceptance. Mirrors the existing
  // attemptFohKickAfterTimer pattern: failure in the custody mutation
  // never rolls back the timer success. Hook's own onError surfaces a
  // destructive toast.
  const attemptCustodyAcceptOnTimerResume = async (): Promise<void> => {
    const eventKind = nextCustodyEventOnTimerStart(custodyRow);
    if (!eventKind) return;
    try {
      await custodyMutation.mutateAsync({
        ticketId,
        eventKind,
        reason: null,
        metadata: { source: 'timer_resume_auto_accept' },
      });
    } catch (e) {
      console.warn('[timer-custody-accept] auto-accept failed after timer success', e);
    }
  };

  /**
   * The ticket-level timer projection this operation learned from the server.
   *
   * `null` means UNKNOWN — either nothing has been read yet, or the read
   * failed. It is never widened into `'stopped'`: "we could not read the
   * projection" is not evidence that work ended, and the custody record this
   * feeds is read later as a statement of fact.
   */
  const lastTimerProjection = useRef<TicketTimerProjection | null>(null);

  /**
   * The RPC payload: human assertions only.
   *
   * `DiagnosticCaptureData` still carries `jobName` and `lockType` because the
   * form displays them. Neither is SENT: the server derives the name and
   * decides whether a job is diagnostic, so a crafted client cannot label an
   * arbitrary job "diagnostic" and record a finding against it.
   */
  const toCaptureAssertions = (
    rows: DiagnosticCaptureData[],
  ): DiagnosticCaptureAssertion[] =>
    rows.map((c) => ({
      jobId: c.jobId,
      outcome: c.outcome,
      competencyOutcome: c.competencyOutcome,
      confidence: c.confidence,
      complaintConfirmed: c.complaintConfirmed,
    }));

  // ═══════════════════════════════════════════════════════════════════════
  // STAGE A — ONE INTERACTION LAYER
  // ═══════════════════════════════════════════════════════════════════════
  // What THIS technician may do comes from their server-read state
  // (deriveTimerWorkState / availableTimerActions). Which human facts an act
  // needs, and in what order, comes from its plan (buildTimerActionPlan). The
  // act reaches the server and is interpreted in ONE place (executeTimerAction
  // → the outcome model), and its success-only effects run through
  // runTimerSuccessEffects. This component renders, collects the human facts,
  // and says what happened — it does not interpret server outcomes, decide the
  // ticket's multi-technician topology, or run effects after a non-success.
  // The chosen scope and bay live here, held across a pre-auth override so the
  // retry is the SAME act; cancelling any step clears them.
  const interactionRef = useRef<TimerInteraction<TimerActFacts> | null>(null);
  if (!interactionRef.current) interactionRef.current = new TimerInteraction<TimerActFacts>();
  const interaction = interactionRef.current;
  const workState = deriveTimerWorkState(myState);
  const planFor = (intent: TimerActionIntent) =>
    buildTimerActionPlan(intent, { state: workState, requiresPullIn });

  /**
   * STAGE 0C — the server refused a work entry for pre-repair authorization.
   *
   * A viewer with the manager override authority gets the override dialog
   * (retrying the SAME act afterwards). Everyone else — a normal technician —
   * gets the truth and no commercial controls: work can't start, and the
   * server has already told the front of house (`fohNotified`).
   */
  const reportWorkEntryBlocked = (
    blocker: 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED',
    fohNotified: boolean,
    retry: 'start' | 'resume',
  ): void => {
    if (canOverridePreauth === true) {
      setPreAuthModal({ open: true, blocker, retry });
      return;
    }
    const verb = retry === 'resume' ? 'resume' : 'start';
    toast.error(
      blocker === 'ESTIMATE_REQUIRED'
        ? `Work can't ${verb} yet — an estimate must be prepared first.`
        : `Work can't ${verb} yet — customer authorization is required.`,
      {
        description: fohNotified
          ? 'FOH has been notified.'
          : 'Please let your service advisor know.',
      },
    );
  };

  /** Say what the server said. The words come from the outcome model. */
  const presentTimerResult = (result: TimerActionResult): void => {
    if (result.kind === 'requires_action' && result.requirement === 'PRE_REPAIR_AUTHORIZATION') {
      reportWorkEntryBlocked(result.blocker, result.fohNotified, result.intent === 'RESUME' ? 'resume' : 'start');
      return;
    }
    const p = result.presentation;
    if (!p) return;
    if ('description' in p) toast[p.tone](p.title, { description: p.description });
    else toast[p.tone](p.title);
  };

  // Ref for dialog content to fix dropdown z-index issue

  // Check for uncaptured diagnostic jobs when pause dialog opens
  // Uses pause-specific function (checks separate_time for time-lock status)
  // 8C: candidates come from the SERVER, scoped to THIS technician's work
  // episode. The old helper asked for every diagnostic job on the TICKET, so on
  // a shared ticket it prompted a technician for a colleague's work.
  useEffect(() => {
    if (isPauseDialogOpen) {
      readMyDiagnosticCaptureCandidates(ticketId)
        .then(candidates => {
          setUncapturedDiagJobs(candidates.map(c => ({
            jobId: c.jobId, jobName: c.jobName, lockType: c.captureKind,
          })));
          setCapturedJobs([]);
          setSkippedJobIds(new Set());
        })
        .catch(err => {
          console.error('Error fetching diagnostic capture candidates:', err);
          setUncapturedDiagJobs([]);
        });
    }
  }, [isPauseDialogOpen, ticketId]);

  // Check for uncaptured diagnostic jobs when stop dialog opens
  // Uses pause-specific function (same as pause - we want jobs not yet time-locked)
  // 8C: same self-scoped candidates. A PAUSED technician gets none — the
  // server returns NO_ACTIVE_TIMER because there is no open interval to attach
  // a capture to, and End Work must not fabricate one.
  // Stage E: what this Stop means — only my work, or the last live BOH work
  // (and whether the vehicle must leave a bay). Advisory: finish_my_work
  // re-decides under the ticket lock and says DESTINATION_REQUIRED if needed.
  useEffect(() => {
    if (!isStopDialogOpen) return;
    let live = true;
    setFinishCtxLoaded(false);
    readMyFinishWorkContext(ticketId)
      .then(async (ctx) => {
        if (!live) return;
        setFinishCtx(ctx);
        if (ctx?.finalWorker && ctx.holdsLocation) {
          // #19 — camera help only where the question is asked (the car is
          // not already parked), read alongside the spots and bounded.
          const cameraRead = ctx.needsDestination ? readLocationAssist(ticketId, 'STOP') : Promise.resolve(NO_LOCATION_ASSIST);
          try {
            const [spots, cameraAssist] = await Promise.all([readParkingSpotChoices(shopId, ticketId), cameraRead]);
            if (!live) return;
            setSpotChoices(spots);
            const camera = assistAnswer(cameraAssist, 'PARKING_SPOT', spots);
            setStopCamera({ foundSpotId: camera.found?.id ?? null, suggestedSpotId: camera.suggested?.id ?? null });
            // "Found it in Spot 12": pre-answered, never over a choice already made.
            if (camera.found) {
              const spotId = camera.found.id;
              setStopDestination((d) => d ?? { kind: 'PARKING_SPOT', parkingSpotId: spotId });
            }
          } catch (e) {
            console.warn('[TicketTimerControls] parking spots unavailable', e);
          }
        }
      })
      .catch((e) => {
        // The server still decides: a Stop that needs a destination answers
        // DESTINATION_REQUIRED and the question appears then.
        console.warn('[TicketTimerControls] finish context unavailable', e);
        if (live) setFinishCtx(null);
      })
      .finally(() => { if (live) setFinishCtxLoaded(true); });
    return () => { live = false; };
  }, [isStopDialogOpen, ticketId, shopId]);

  useEffect(() => {
    if (isStopDialogOpen) {
      readMyDiagnosticCaptureCandidates(ticketId)
        .then(candidates => {
          setUncapturedDiagJobsForStop(candidates.map(c => ({
            jobId: c.jobId, jobName: c.jobName, lockType: c.captureKind,
          })));
          setCapturedJobsForStop([]);
          setSkippedJobIdsForStop(new Set());
        })
        .catch(err => {
          console.error('Error fetching diagnostic capture candidates:', err);
          setUncapturedDiagJobsForStop([]);
        });
    }
  }, [isStopDialogOpen, ticketId]);

  /**
   * Re-read the caller's own state.
   *
   * DISPLAY ONLY. Every action revalidates server-side, so a stale read here
   * can never make a wrong action correct — `start_my_timer_with_scope`
   * returning ALREADY_HAS_ACTIVE_TIMER is the authority, and this component
   * simply reports what it says.
   */
  const refreshMyState = useCallback(async () => {
    setMyState(await readMyTimerControlState(ticketId));
  }, [ticketId]);

  useEffect(() => {
    void refreshMyState();
    // `timerStatus` is included so a parent-driven ticket refresh also
    // re-reads THIS technician's state; the ticket moving is usually the
    // moment their own state moved too.
  }, [refreshMyState, timerStatus]);

  // ── #3 U0/U1: a review with nothing to decide is resolved by the SERVER ──
  // ONE attempt per episode for this control, shared by the Stop that created
  // the review and the pending-review check below, so neither calls twice nor
  // shows the line twice. The server classifies from its frozen review and
  // writes through finalize with no completion ids; the browser decides
  // nothing. Any failure answers REVIEW_REQUIRED — the ordinary Review Time.
  const trivialAttempts = useRef(new Map<string, Promise<TrivialReviewResult>>());
  const attemptTrivialReview = useCallback((episodeId: string) => {
    let attempt = trivialAttempts.current.get(episodeId);
    if (!attempt) {
      attempt = acceptTrivialTimerReview(episodeId).then((r) => {
        const notice = trivialReviewNotice(r);
        if (notice) toast.success(notice);
        return r;
      });
      trivialAttempts.current.set(episodeId, attempt);
    }
    return attempt;
  }, []);
  const onTimerUpdateRef = useRef(onTimerUpdate);
  onTimerUpdateRef.current = onTimerUpdate;

  /** After a Stop / End Work: Review Time only when the allocation is ambiguous. */
  const resolveOrOpenReview = useCallback(async (episodeId: string | null | undefined) => {
    if (!episodeId) return;
    const r = await attemptTrivialReview(episodeId);
    if (r.kind === 'REVIEW_REQUIRED') {
      openReview(episodeId);
      return;
    }
    await refreshMyState();
    onTimerUpdateRef.current();
  }, [attemptTrivialReview, openReview, refreshMyState]);

  // A review already waiting when this control loads (voice Stop, a review
  // from before #3, a technician coming back): one attempt per episode. A
  // genuine review keeps the Review Time button; nothing opens by itself.
  const pendingReviewEpisodeId = workState === 'awaiting_review' ? myState.workEpisodeId ?? null : null;
  useEffect(() => {
    if (!pendingReviewEpisodeId) return undefined;
    let live = true;
    void attemptTrivialReview(pendingReviewEpisodeId).then(async (r) => {
      if (!live || r.kind === 'REVIEW_REQUIRED') return;
      await refreshMyState();
      onTimerUpdateRef.current();
    });
    return () => { live = false; };
  }, [pendingReviewEpisodeId, attemptTrivialReview, refreshMyState]);

  /**
   * The open job lines this Start may scope, from the server
   * (`my_startable_jobs`). For a rollover continuation they are the REPAIR's
   * lines, owned by the chain's first ticket; the server resolves that owner —
   * the client never names it, and Start re-validates every selection.
   */
  const loadStartableJobs = async (): Promise<StartableJob[]> => {
    const { data, error } = await (supabase as any).rpc('my_startable_jobs', { p_ticket_id: ticketId });
    const r = (data ?? null) as {
      outcome?: string;
      continued_work?: boolean;
      owner_ticket_number?: number | null;
      jobs?: Array<{ job_id: string; name: string | null; minutes_override: number | null; standard_minutes: number | null }>;
    } | null;
    if (error || r?.outcome !== 'OK') {
      setContinuedFrom(null);
      return [];
    }
    setContinuedFrom(r.continued_work
      ? (r.owner_ticket_number != null ? `TKT-${String(r.owner_ticket_number).padStart(4, '0')}` : 'the first day')
      : null);
    return (r.jobs ?? []).map((j) => ({
      jobId: j.job_id,
      name: j.name ?? 'Job',
      // Same authority the rest of Core uses for expected time.
      expectedMinutes: j.minutes_override ?? j.standard_minutes ?? null,
    }));
  };

  /**
   * Stage C — WORK_TARGET: where does this Start's work happen?
   *
   * Read fresh from OptiBay's own facts (current physical location, the plan,
   * physical occupancy, bays refused in this act) and decided by
   * guidedStart.ts. A car already in its bay needs no question; a car in a
   * bay other than its planned one surfaces the mismatch; a parked car with a
   * usable planned bay gets the preview + countdown; otherwise the technician
   * chooses. The SERVER re-validates every target at commit.
   */
  const resolveWorkTargetStep = async () => {
    // Oct 20 (D-4): Resume of parked paused work asks only "which bay?" (no countdown, no scope question).
    if (interaction.current.intent === 'RESUME') {
      await resolveResumeTargetStep();
      return;
    }
    setIsLoading(true);
    let ctx: GuidedStartContext;
    try {
      ctx = await readGuidedStartContext({
        ticketId,
        shopId,
        legacyParked: requiresPullIn,
        refusedBayIds: interaction.current.facts.refusedBayIds ?? [],
      });
    } catch (e) {
      console.error('[TicketTimerControls] could not read where the vehicle is', e);
      setIsLoading(false);
      if (!requiresPullIn) {
        // Nothing needs moving: the ordinary Start asks the server, which
        // decides presence itself.
        interaction.complete('WORK_TARGET', { target: null });
        await advanceAct();
        return;
      }
      toast.error('Could not read where this vehicle is', { description: 'Nothing moved and nothing started.' });
      interaction.cancel();
      return;
    }
    // #19 — camera help, read ONLY when the technician would otherwise be
    // asked which bay (a parked car), bounded (never throws, never waits past
    // LOCATION_ASSIST_TIMEOUT_MS). No answer = exactly the old question.
    const asksBay = (k: GuidedStartDecision['kind']) => k === 'CHOOSE_BAY' || k === 'MOVE_PLANNED';
    if (asksBay(decideGuidedStart(ctx).kind)) {
      ctx = { ...ctx, cameraAssist: await readLocationAssist(ticketId, 'START').catch(() => NO_LOCATION_ASSIST) };
    }
    setIsLoading(false);

    const decision = decideGuidedStart(ctx);
    // Item #14 — the floor plan, read only when a bay is to be shown. It is
    // presentation: unreadable → the List is the selector, nothing else changes.
    const layoutRead = () => readFacilityLayout(shopId).catch((e) => {
      console.warn('[TicketTimerControls] shop floor layout unavailable; using the bay list', e);
      return null;
    });
    switch (decision.kind) {
      case 'IN_PLACE':
        interaction.complete('WORK_TARGET', { target: null });
        await advanceAct();
        return;
      case 'NOT_ON_SITE':
      case 'LOCATION_NOT_RECORDED':
        toast.error(decision.title, { description: `${decision.message} Nothing moved and nothing started.` });
        interaction.cancel();
        return;
      case 'MOVE_PLANNED':
        setGuided({ mode: 'countdown', decision, choices: guidedBayChoices(ctx), vehicleLabel: ctx.vehicleLabel, layout: await layoutRead() });
        return;
      case 'CHOOSE_BAY':
        setGuided({ mode: 'choose', decision, choices: guidedBayChoices(ctx), vehicleLabel: ctx.vehicleLabel, layout: await layoutRead() });
        return;
      case 'CAMERA_FOUND':
        // "Found it in Bay 3" — confirmed with one tap through the SAME
        // pull-in as a chosen bay (commitGuidedStart), or changed.
        setGuided({ mode: 'found', decision, choices: guidedBayChoices(ctx), vehicleLabel: ctx.vehicleLabel, layout: await layoutRead() });
        return;
      case 'BAY_MISMATCH':
        setGuided({ mode: 'mismatch', decision, choices: [], vehicleLabel: ctx.vehicleLabel, layout: null });
        return;
      default: {
        const never: never = decision;
        return never;
      }
    }
  };

  const [preAuthModal, setPreAuthModal] = useState<{
    open: boolean;
    blocker: 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED';
    retry: 'start' | 'resume';
  }>({ open: false, blocker: 'AUTHORIZATION_REQUIRED', retry: 'start' });

  // ── SUCCESS-ONLY EFFECTS (shared by every act) ─────────────────────────────
  // runTimerSuccessEffects calls these only after a confirmed success, in the
  // act's plan order. A refusal, a failure or an unknown outcome runs none.
  const commonEffects: TimerSuccessEffectHandlers = {
    REFRESH_MY_STATE: () => refreshMyState(),
    REFRESH_BOARD: () => onTimerUpdate(),
    MARKETING_NOTICE: () => {
      if (isMarketingMode && customerName) notifyTimerStarted(customerName);
    },
    // Auto-clear pending_tech_accept custody once the tech is timing again.
    // Fire-and-forget; timer success is authoritative.
    CUSTODY_ACCEPT: () => { void attemptCustodyAcceptOnTimerResume(); },
    // 8E: Stop / End Paused Work freezes a proposal, so offer the review —
    // for a committed boundary with a real episode only. Opened BEFORE the
    // optional FOH handoff, so a failed kick cannot prevent it (8E.1).
    // A final Stop that COMPLETED the ticket: this card leaves the Dayboard on
    // the next refetch and would take the dialog with it, so the review is
    // offered by the app-level host instead. Still reachable later from the
    // completed ticket's review-only control.
    // #3: the server first resolves a review with nothing to decide (U0/U1);
    // only an ambiguous one opens Review Time (the host does the same).
    OPEN_REVIEW: (r) => (r.ticketCompleted
      ? openTimerReviewOutsideCard(r.reviewEpisodeId)
      : void resolveOrOpenReview(r.reviewEpisodeId)),
  };

  /**
   * Submit the act the interaction collected. The ONE place this surface
   * reaches a timer authority: single-flight (a second click while one act is
   * in flight submits nothing), one outcome model, success-only effects.
   */
  const submitTimerAct = async (
    request: TimerActionRequest,
    handlers: TimerSuccessEffectHandlers,
  ): Promise<TimerActionResult | null> => {
    if (interaction.submit() === null) return null;
    setIsLoading(true);
    setIsPullingIn((request.intent === 'START' || request.intent === 'RESUME') && !!request.target);
    try {
      const result = await executeTimerAction(request);
      if (result.kind === 'success') lastTimerProjection.current = result.ticketTimerStatus;
      presentTimerResult(result);
      // W5: one hands-on timer at a time — after the server refused a second one, offer the atomic switch
      // (WorkSwitchHost on the Dashboard; a no-op where it is not mounted). The refusal itself is unchanged and the
      // classification lives in the timer lib, not in this surface.
      offerWorkSwitchAfterRefusal(request, result, shopId ?? null);
      if (result.kind === 'success') {
        await runTimerSuccessEffects(result, planFor(request.intent).successEffects, handlers);
      } else if (result.refreshMyState) {
        // The server contradicted what was on screen: the correction is the point.
        await refreshMyState();
      }
      return result;
    } catch (e) {
      // Something after the send threw (presentation, a refresh). Never leave
      // the gate held: a stuck 'submitting' would silently disable every act.
      interaction.settle();
      throw e;
    } finally {
      setIsLoading(false);
      setIsPullingIn(false);
    }
  };

  // ── START / RESUME: plan-driven input steps ────────────────────────────────
  /**
   * Oct 20 (D-4) — the server said the paused work's vehicle is parked. Ask which bay to bring it into; the SAME
   * Resume is then resubmitted with that target (pull_in_and_resume_my_timer: one atomic move + resume). Nothing
   * changes until the technician commits; the server re-validates the bay.
   */
  const resolveResumeTargetStep = async () => {
    setIsLoading(true);
    try {
      const ctx = await readGuidedStartContext({
        ticketId,
        shopId,
        legacyParked: true,
        refusedBayIds: interaction.current.facts.refusedBayIds ?? [],
      });
      const layout = await readFacilityLayout(shopId).catch(() => null);
      setGuided({
        mode: 'choose', act: 'resume', decision: resumeBayChoiceDecision(ctx),
        choices: guidedBayChoices(ctx), vehicleLabel: ctx.vehicleLabel, layout,
      });
    } catch (e) {
      console.error('[TicketTimerControls] could not read where the parked vehicle is', e);
      toast.error('Could not read where this vehicle is', { description: 'Nothing moved; your work is still paused.' });
      interaction.cancel();
    } finally {
      setIsLoading(false);
    }
  };

  /** Begin an act from its plan. Nothing is sent until its human facts are in. */
  const beginAct = (intent: 'START' | 'RESUME', steps?: readonly TimerInputStep[]) => {
    const plan = planFor(intent);
    if (!plan.allowed) return;
    if (!interaction.begin(intent, steps ?? plan.steps)) return;   // an act is in flight
    void advanceAct();
  };

  /** Show the next input step; submit once none is left. */
  const advanceAct = async (): Promise<void> => {
    const { intent } = interaction.current;
    switch (interaction.step) {
      case 'SERVICE_INFO':
        // A PARKED Start is still a Start: the acknowledgement comes first,
        // then scope, and only then — for a parked vehicle — the bay.
        setShowServiceInfoPrompt(true);
        return;
      case 'JOB_SCOPE':
        await resolveJobScopeStep();
        return;
      case 'WORK_TARGET':
        await resolveWorkTargetStep();
        return;
      case 'EOD_DECISION':
        await resolveEodDecisionStep();
        return;
      case null:
        if (intent === 'START') await submitStart();
        else if (intent === 'RESUME') await submitResume();
        return;
      default:
        // PAUSE_DETAILS / STOP_DETAILS are dialogs submitted by their own confirm.
        return;
    }
  };

  const handleStartClick = () => beginAct('START');
  const handleResumeClick = () => beginAct('RESUME');

  /** Walking away from any input step abandons the act: nothing sent, nothing remembered. */
  const cancelAct = () => {
    interaction.cancel();
    setShowServiceInfoPrompt(false);
    setGuided(null);
  };

  const handleServiceInfoComplete = async (data: ServiceInfoData) => {
    setShowServiceInfoPrompt(false);
    // Fire-and-forget OCIS log
    import('@/utils/ticketEventLogger').then(({ logServiceInfoAcknowledgment }) => {
      supabase.auth.getUser().then(({ data: { user } }) => {
        logServiceInfoAcknowledgment({
          ticketId,
          // Canonical where the server has already told us; the props are the
          // fallback for a state that could not be read.
          shopId: myState.shopId ?? shopId,
          userId: user?.id || '',
          techId: myState.technicianId ?? techId ?? '',
          acknowledged: data.acknowledged,
          sources: data.sources,
          note: data.note,
          // The act THIS technician is performing, not the ticket's state: a
          // ticket reads `running` while this technician is paused.
          sessionContext: interaction.current.intent === 'RESUME' ? 'resume' : 'start',
        });
      });
    });
    interaction.complete('SERVICE_INFO');
    await advanceAct();
  };

  /**
   * JOB_SCOPE — the technician's assertion of what the time is for.
   *
   *   0 jobs  -> no timer at all. There is nothing to attribute the time to.
   *   1 job   -> that job IS the selection. A one-item picker asks a question
   *              with one possible answer.
   *   2+ jobs -> ask, starting from NOTHING selected.
   *
   * The empty initial selection is the point. A preselected list that the
   * technician merely fails to change is passive acceptance, and passive
   * acceptance is not evidence of who worked on what.
   */
  const resolveJobScopeStep = async () => {
    setIsLoading(true);
    try {
      const jobs = await loadStartableJobs();
      if (jobs.length === 0) {
        toast.error('No open jobs to work on', {
          description:
            'This ticket has no open job lines, so there is nothing to attribute time to.',
        });
        interaction.cancel();
        return;
      }
      if (jobs.length === 1) {
        interaction.complete('JOB_SCOPE', { jobIds: [jobs[0].jobId] });
        await advanceAct();
        return;
      }
      setStartableJobs(jobs);
      setSelectedJobIds(new Set());
      setScopeDialogOpen(true);
    } catch (e) {
      console.error('[TicketTimerControls] could not load startable jobs', e);
      toast.error('Could not load this ticket’s jobs');
      interaction.cancel();
    } finally {
      setIsLoading(false);
    }
  };

  /** The scope dialog's confirm. */
  const completeJobScope = async (jobIds: string[]) => {
    if (jobIds.length === 0) return;
    if (interaction.step !== 'JOB_SCOPE') {
      // The dialog outlived the act it was opened for (the server refused
      // that Start): this confirm is a new Start with the new selection.
      if (!interaction.begin('START', planFor('START').steps.filter((s) => s !== 'SERVICE_INFO'))) return;
    }
    interaction.complete('JOB_SCOPE', { jobIds });
    await advanceAct();
  };

  /**
   * Stage C — commit the Guided Start into `bayId` (countdown reached zero,
   * "Start now", or a chosen bay confirmed). Only while the act is waiting on
   * WORK_TARGET: a second call — double tap, or zero racing "Start now" —
   * finds the step already completed and does nothing.
   */
  const commitGuidedStart = async (bayId: string) => {
    if (interaction.step !== 'WORK_TARGET') return;
    // A Resume carries its scope forward from the paused episode; only a Start asks for jobs.
    if (interaction.current.intent !== 'RESUME' && !(interaction.current.facts.jobIds?.length)) {
      toast.error('Choose what you are working on first.');
      return;
    }
    setGuided(null);
    interaction.complete('WORK_TARGET', { target: { kind: 'bay', bayId } });
    await advanceAct();
  };

  /** "Different bay": stop the countdown and let the technician choose. */
  const chooseDifferentBay = () => {
    setGuided((g) => (g && g.decision.kind !== 'BAY_MISMATCH' ? {
      ...g,
      mode: 'choose',
      decision: {
        kind: 'CHOOSE_BAY', fromLabel: g.decision.fromLabel, whereNow: g.decision.whereNow,
        reason: 'NO_PLAN', plannedBayId: null, plannedLabel: null,
      },
    } : g));
  };

  /**
   * Mismatch → "Start in <current bay>": the technician's explicit choice to
   * work where the car IS. The ordinary Start; nothing moves.
   */
  const startHereDespitePlan = async () => {
    if (interaction.step !== 'WORK_TARGET') return;
    setGuided(null);
    interaction.complete('WORK_TARGET', { target: null });
    await advanceAct();
  };

  /**
   * Mismatch → "Move it first": this Start ends with nothing changed. A
   * bay-to-bay move is a standalone relocation (not a technician's act, and
   * not part of pull_in_and_start_my_timer), so it is never chained here.
   */
  const moveFirstInstead = () => {
    const d = guided?.decision;
    cancelAct();
    if (d?.kind === 'BAY_MISMATCH') {
      toast.info(`Move it to ${d.plannedLabel} first`, {
        description: `Nothing moved and nothing started. Once the vehicle is in ${d.plannedLabel}, Start begins work there.`,
      });
    }
  };

  /** Fire the canonical Start with the collected scope (and target). */
  const submitStart = async () => {
    const { jobIds = [], target = null } = interaction.current.facts;
    if (jobIds.length === 0) { interaction.cancel(); return; }
    const result = await submitTimerAct(
      {
        intent: 'START',
        ticketId,
        jobIds,
        // Guided Start decided it: a bay target means the ONE atomic
        // move-and-start; none means the car is already where work happens.
        target,
        skipTimeUpdate: isMarketingMode,
      },
      commonEffects,
    );
    if (!result) return;
    if (result.kind === 'success') {
      interaction.settle();
      setScopeDialogOpen(false);
      return;
    }
    if (result.kind === 'requires_action' && result.requirement === 'PRE_REPAIR_AUTHORIZATION') {
      // An override retries the SAME act: same jobs, same bay. Re-deriving
      // either would replace the technician's assertion with a guess.
      interaction.settle({ keepFacts: true });
      return;
    }
    if (result.kind === 'refused' && result.retryTarget && target) {
      // STAGE 0D / C — a refused bay is never retried and never silently
      // replaced. Nothing moved and nothing started (one atomic act). Remember
      // it for THIS act, re-read occupancy, and ask again: the technician
      // chooses, never the client.
      interaction.settle({ keepFacts: true });
      interaction.remember({ refusedBayIds: [...(interaction.current.facts.refusedBayIds ?? []), target.bayId] });
      interaction.reask('WORK_TARGET', ['target']);
      await advanceAct();
      return;
    }
    interaction.settle();
  };

  /**
   * EOD_DECISION — would resuming run past this technician's scheduled day?
   *
   * The end-of-day question is asked about the AUTHENTICATED resumer, not
   * whoever the ticket is assigned to. When no shop-floor identity resolves
   * the check is SKIPPED rather than run with a bad id (`checkEODViolation`
   * answers "fine" for an unknown technician); `resume_my_timer` refuses a
   * non-technician itself a moment later.
   */
  const resolveEodDecisionStep = async () => {
    setIsLoading(true);
    try {
      // OCT28 WP-2b-2b-ii-b2-A (founder decision, OPTION B + Option a): the database's end-of-day judgement
      // (ticket_eod_status), read FRESH at this click — never a cached answer, never a browser estimate from elapsed
      // time minus worked time.
      const { data: ticketData } = await supabase
        .from('tickets')
        .select('customer_name, actual_start_time, parent_ticket_id, sequence_index, split_reason, rollover_of_ticket_id')
        .eq('id', ticketId)
        .single();

      const eodTechId = myState.technicianId ?? techId ?? null;
      if (ticketData?.actual_start_time && eodTechId && shopId) {
        const [{ verdict }, { data: prof }] = await Promise.all([
          loadServerEodVerdict(ticketId, shopId),
          supabase.from('shop_profile').select('time_zone').eq('shop_id', shopId).maybeSingle(),
        ]);
        const timeZone = (prof as { time_zone?: string | null } | null)?.time_zone ?? undefined;
        const action = resumeEodAction(verdict);

        if (action === 'proceed_unconfirmed' && verdict.state === 'unconfirmed') {
          // Option (a): recording the work goes ahead (every other validation still runs in resume_my_timer); it
          // authorizes no overtime, rollover or reservation. The check stays open in this tab and is re-read when
          // the server's facts change; a confirmed overrun then raises the existing EOD decision.
          markEodPendingVerification(shopId, ticketId, techId ?? null, new Date(clock.nowMs()).toISOString());
          toast.warning('End-of-day check not confirmed', {
            description: describeUnconfirmed(verdict, (d) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(d))
              + ' Resuming records your work; it does not approve overtime or a rollover.',
          });
        }

        if (action === 'decision_required' && verdict.state === 'conflict' && isPartOfRolloverChain(ticketData) && !verdict.conversionId) {
          // A confirmed overrun on a multi-day repair: the conversion dialog cannot represent a chain row, so the
          // work is recorded (as before for chain rows) and the decision is shown as OUTSTANDING — not approved, not hidden.
          toast.warning('End-of-day decision outstanding', {
            description: describeEodConflict(verdict.overrunMin, 'chain_unrepresentable')
              + ' Resuming records your work; it does not approve overtime or a rollover.',
          });
        } else if (action === 'decision_required' && verdict.state === 'conflict') {
          // A CONFIRMED overrun: someone with placement authority decides; their "Resume Anyway" re-enters the SAME
          // executor as a fresh Resume.
          const boundary = verdict.boundaryAt ? new Date(verdict.boundaryAt) : null;
          window.dispatchEvent(new CustomEvent('eod-exceeded', {
            detail: {
              exceeds: true,
              rolloverMinutes: verdict.overrunMin,
              techEOD: boundary && timeZone
                ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).format(boundary)
                : null,
              source: 'server_eod_status',
              decisionKey: verdict.decisionKey,
              ticketId,
              customerName: ticketData.customer_name || customerName || 'Unknown Customer',
              onResumeAnyway: async () => {
                if (!interaction.begin('RESUME', [])) return;
                await submitResume();
              },
            },
          }));

          // STAGE 0B. The event above is heard only by viewers with placement
          // authority (EODRolloverContainer registers no listener otherwise),
          // so a technician is told the truth and who makes the decision.
          // Nothing is resumed and no schedule is changed.
          if (canDecideEndOfDay !== true) {
            toast.warning('Resuming now would run past the end of your scheduled day', {
              description:
                'Your timer was not resumed. A service advisor or manager needs to extend your day or roll this job over before you continue.',
            });
          }
          interaction.cancel();
          return;
        }
      }
      interaction.complete('EOD_DECISION');
      await advanceAct();
    } catch (error) {
      console.error("Error resuming timer:", error);
      toast.error("Failed to resume timer");
      interaction.cancel();
    } finally {
      setIsLoading(false);
    }
  };

  const submitResume = async () => {
    const target = interaction.current.facts.target ?? null;
    const result = await submitTimerAct(
      { intent: 'RESUME', ticketId, techId: techId ?? undefined, shopId, target },
      commonEffects,
    );
    if (!result) return;
    if (result.kind === 'requires_action' && result.requirement === 'PULL_IN_TO_BAY') {
      // Oct 20 (D-4): parked — guide the technician to a bay; nothing changed.
      interaction.settle({ keepFacts: true });
      interaction.reask('WORK_TARGET', ['target']);
      await advanceAct();
      return;
    }
    if (result.kind === 'refused' && result.retryTarget && target) {
      // The chosen bay was refused: nothing moved, nothing resumed. Remember it and ask again.
      interaction.settle({ keepFacts: true });
      interaction.remember({ refusedBayIds: [...(interaction.current.facts.refusedBayIds ?? []), target.bayId] });
      interaction.reask('WORK_TARGET', ['target']);
      await advanceAct();
      return;
    }
    interaction.settle();
  };

  /** After a manager override, retry the SAME act that was refused. */
  const retryAfterOverride = async () => {
    if (preAuthModal.retry === 'resume') {
      // The acknowledgement was given; the end-of-day question is asked again.
      beginAct('RESUME', ['EOD_DECISION']);
      return;
    }
    const { intent, facts } = interaction.current;
    if (intent === 'START' && facts.jobIds && facts.jobIds.length > 0) {
      if (!interaction.begin('START', [], facts)) return;
      await submitStart();
      return;
    }
    beginAct('START', planFor('START').steps.filter((s) => s !== 'SERVICE_INFO'));
  };

  // ── PAUSE ──────────────────────────────────────────────────────────────────
  const handlePause = () => {
    if (!planFor('PAUSE').allowed || !interaction.begin('PAUSE', planFor('PAUSE').steps)) return;
    setIsPauseDialogOpen(true);
  };

  const handleJobCapture = useCallback((data: DiagnosticCaptureData) => {
    setCapturedJobs(prev => {
      // Replace if already exists, otherwise add
      const existing = prev.findIndex(c => c.jobId === data.jobId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = data;
        return updated;
      }
      return [...prev, data];
    });
    // Remove from skipped if it was there
    setSkippedJobIds(prev => {
      const next = new Set(prev);
      next.delete(data.jobId);
      return next;
    });
  }, []);

  const handleJobSkip = useCallback((jobId: string) => {
    setSkippedJobIds(prev => new Set(prev).add(jobId));
    // Remove from captured if it was there
    setCapturedJobs(prev => prev.filter(c => c.jobId !== jobId));
  }, []);

  // FOH Timer Handoff — Batch 2. Fire-and-forget secondary mutation
  // invoked AFTER a successful timer mutation. Never throws to the
  // caller (timer success must remain authoritative). The custody
  // mutation hook's own onError surfaces a destructive toast so the
  // user sees the FOH-kick failure explicitly; this wrapper just
  // ensures we don't accidentally roll back the timer success or
  // double-warn.
  const attemptFohKickAfterTimer = async (
    trigger: 'pause_kick' | 'stop_kick',
    reasonValue: string,
    // The ACTUAL ticket-level timer projection, which may still be 'running'
    // when another technician is working. "I stopped/paused MY timer" is not
    // "the ticket is stopped/paused".
    timerStatusAt: 'running' | 'paused' | 'stopped' | null,
    extraPauseReason: string | null,
    /** Stage D: the structured Pause answer, carried to the custody record. */
    extra?: Record<string, unknown>,
    notes?: string | null,
  ): Promise<void> => {
    if (!reasonValue) return;
    const reasonLabel = getCustodyHandoffReasonLabel(reasonValue);
    const metadata = buildCustodyHandoffMetadata({
      trigger,
      reason: reasonValue,
      reasonLabel,
      timerStatusAt,
      pauseReason: trigger === 'pause_kick' ? extraPauseReason : undefined,
      sourceComponent: 'TicketTimerControls',
      notes: notes ?? undefined,
      extra,
    });
    try {
      await custodyMutation.mutateAsync({
        ticketId,
        eventKind: 'kick_to_foh',
        reason: reasonValue,
        metadata,
      });
    } catch (e) {
      // Hook's onError already showed a destructive toast — log for
      // dev triage but do NOT re-throw (timer success is preserved).
      console.warn(
        '[timer-foh-kick] custody mutation failed after timer success',
        e,
      );
    }
  };

  /**
   * FOH_HANDOFF after a committed Pause — Stage D: driven by the ONE answer.
   *
   * Two different things, never conflated:
   *   attention  "the advisor / a manager needs to respond" — raised by the
   *              SERVER in the pause's own transaction (result.pauseAttention);
   *   custody    "FOH now owns the whole ticket" — only an advisor/customer
   *              answer asks for it, and STAGE 0B still decides it: only when
   *              the server says this pause left no other live BOH work (a
   *              paused colleague still counts). Technical help never does.
   */
  const handOffAfterPause = async (
    detail: PauseDetail, note: string | null, result: TimerActionSuccess,
  ): Promise<void> => {
    const option = pauseOption(detail);
    const fohDecision = decidePauseFohHandoff(pauseRequestsFohCustody(detail), {
      succeeded: true, pauseLeftNoOtherLiveWork: result.pauseLeftNoOtherLiveWork,
    });
    if (fohDecision === 'kick' && option?.custodyReason) {
      await attemptFohKickAfterTimer('pause_kick', option.custodyReason, lastTimerProjection.current,
        option.pauseReason, { pause_detail: detail, pause_intent: option.intent }, note);
    }
    const message = pauseAttentionMessage(result.pauseAttention, fohDecision);
    if (message) toast.info(message);
  };

  const closePauseDialog = () => {
    setIsPauseDialogOpen(false);
    setPauseDetail('');
    setPauseNote('');
    setCapturedJobs([]);
    setSkippedJobIds(new Set());
    setPendingDiagData([]);
  };

  const handlePauseConfirm = async () => {
    // A second tap while the first is in flight sends nothing (the submission
    // gate in submitTimerAct refuses it too); a refusal keeps the act for a retry.
    if (interaction.busy) return;
    if (!pauseDetail) {
      toast.error('Choose why you are stopping work');
      return;
    }

    // Stage D: diagnostic results are OPTIONAL at Pause. Only forms the
    // technician fully answered are sent — captured, or filled in and not yet
    // confirmed; nothing is defaulted and nothing unanswered blocks the pause.
    const effectiveCapturedJobs = [...capturedJobs];
    for (const pending of pendingDiagData) {
      if (!effectiveCapturedJobs.some(c => c.jobId === pending.jobId)) {
        effectiveCapturedJobs.push(pending);
      }
    }

    // Snapshots: the post-success reset below must not drop them out from
    // under the awaited handoff.
    const detail = pauseDetail;
    const note = normalizePauseNote(pauseNote);

    interaction.complete('PAUSE_DETAILS');
    // 8C: with findings, ONE combined server call (never browser-side minute
    // splitting); without, the plain pause. The executor picks the authority.
    const result = await submitTimerAct(
      {
        intent: 'PAUSE',
        ticketId,
        pause: { detail, note },
        diagnosticCaptures: toCaptureAssertions(effectiveCapturedJobs),
      },
      {
        ...commonEffects,
        REFRESH_BOARD: () => { closePauseDialog(); onTimerUpdate(); },
        FOH_HANDOFF: (r) => handOffAfterPause(detail, note, r),
      },
    );
    if (!result) return;
    // A refusal keeps the dialog and its inputs so the technician can act on it.
    interaction.settle({ keepFacts: result.kind !== 'success' && planFor('PAUSE').keepInputsOnRefusal });
  };

  // ── STOP / END WORK ────────────────────────────────────────────────────────
  const handleStopClick = () => {
    // A PAUSED technician is ENDING WORK, not stopping a timer: there is no
    // open session, and resume-then-stop would fabricate an interval.
    const intent = workState === 'paused' ? 'END_WORK' : 'STOP';
    if (!planFor(intent).allowed || !interaction.begin(intent, planFor(intent).steps)) return;
    setIsStopDialogOpen(true);
  };

  const handleJobCaptureForStop = useCallback((data: DiagnosticCaptureData) => {
    setCapturedJobsForStop(prev => {
      const existing = prev.findIndex(c => c.jobId === data.jobId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = data;
        return updated;
      }
      return [...prev, data];
    });
    setSkippedJobIdsForStop(prev => {
      const next = new Set(prev);
      next.delete(data.jobId);
      return next;
    });
  }, []);

  const handleJobSkipForStop = useCallback((jobId: string) => {
    setSkippedJobIdsForStop(prev => new Set(prev).add(jobId));
    setCapturedJobsForStop(prev => prev.filter(c => c.jobId !== jobId));
  }, []);

  /**
   * FOH_HANDOFF after a committed Stop / End Work — Stage E.
   *
   * The handoff itself already happened on the SERVER, in the same
   * transaction, and only because this was the last live BOH work (STAGE 0A:
   * a colleague running or paused keeps the ticket with the shop). This only
   * follows it up — cache, OCIS timeline, advisor action — from the committed
   * custody event. It never transitions custody itself, and never notifies
   * (the server wrote the one notification).
   */
  const handOffAfterStop = async (result: TimerActionSuccess, endingPausedWork: boolean): Promise<void> => {
    if (result.fohHandoffEventId) {
      custodyFollowUp({
        ticketId, eventKind: 'kick_to_foh', reason: FINAL_STOP_HANDOFF_REASON,
        metadata: { trigger: 'stop_kick', trigger_reason: FINAL_STOP_HANDOFF_REASON, source: 'custody_transition_finish_work',
                    timer_status_at: result.ticketTimerStatus, vehicle_moved: result.vehicleMoved },
      }, result.fohHandoffEventId, { toast: false });
    } else if (!result.ticketBecameIdle) {
      toast.info(
        endingPausedWork
          ? 'Your paused work was ended. The ticket remains with the shop because another technician still has work in progress.'
          : 'Your work was stopped. The ticket remains with the shop because another technician still has work in progress.',
      );
    }
  };

  /** Close, forgetting everything this Stop collected — nothing survives into the next attempt. */
  const closeStopDialog = () => {
    setIsStopDialogOpen(false);
    setCapturedJobsForStop([]);
    setSkippedJobIdsForStop(new Set());
    setPendingDiagDataForStop([]);
    setFinishCtx(null);
    setFinishCtxLoaded(false);
    setSpotChoices([]);
    setStopDestination(null);
    setStopCamera(NO_STOP_CAMERA);
    setForceLocationQuestion(false);
    setRefusedSpotIds(new Set());
    setServerUnresolvedJobs([]);
    setJobDispositions({});
    setUnfinishedJobIds(new Set());
    setStopScope(null);
    setSessionConclusions({});
    setServerConclusionJobs([]);
  };

  const stopMode: StopDialogMode | null = finishCtx ? stopDialogMode(finishCtx) : null;
  /** The location answer is REQUIRED (the server needs it); otherwise it is optional. */
  const stopNeedsDestination = forceLocationQuestion || stopMode === 'FINAL_ASK_LOCATION';
  const stopDestinationFinal = draftToDestination(stopDestination);
  const stopChoiceOffered = offersEntireRepairChoice(finishCtx);
  const stopEnds = stopEndsRepair(finishCtx, stopScope);
  // OCT28 #14-c: on a Stop that ends the repair every open inspection is ANSWERED (Completed / Incomplete, or Not
  // performed when it has no recorded work) — never completed by the Stop on its own, and never through the
  // diagnostic findings form (a clean inspection has no findings to record).
  const endingInspections: Array<{ jobId: string; name: string; kind: 'inspection'; worked?: boolean }> = stopEnds
    ? [
        ...uncapturedDiagJobsForStop.filter((j) => j.lockType === 'inspection')
          .map((j) => ({ jobId: j.jobId, name: j.jobName, kind: 'inspection' as const, worked: true })),
        ...(finishCtx?.openJobs ?? []).filter((j) => j.kind === 'inspection' && !j.inMyScope)
          .map((j) => ({ jobId: j.jobId, name: j.name, kind: 'inspection' as const })),
      ]
    : [];
  const stopFindingsJobs = stopEnds
    ? uncapturedDiagJobsForStop.filter((j) => j.lockType !== 'inspection') : uncapturedDiagJobsForStop;
  /** Every job the final Stop needs an answer for: the advisory list plus any the server named. */
  const unresolvedStopJobs: DispositionJob[] = [];
  const asked: DispositionJob[] = [...(finishCtx?.unscopedJobs ?? []), ...endingInspections, ...serverUnresolvedJobs];
  for (const j of asked) {
    const at = unresolvedStopJobs.findIndex((u) => u.jobId === j.jobId);
    if (at < 0) unresolvedStopJobs.push(j);
    // the server's facts (kind / worked) win over the advisory list
    else if (j.worked !== undefined) unresolvedStopJobs[at] = { ...unresolvedStopJobs[at], ...j };
  }

  /**
   * Findings to send with the Stop: the ones confirmed, plus any form the
   * technician has FULLY answered but not yet confirmed (the same rule Pause
   * uses). Core gate: a fully answered form used to leave Stop disabled with
   * no hint until "Confirm Diagnostic" was pressed. A form is only pending
   * once every answer — including confidence and complaint — was given
   * explicitly (DiagnosticPauseCapture), so nothing is inferred.
   */
  const effectiveStopCaptures = (): DiagnosticCaptureData[] => [
    ...capturedJobsForStop,
    ...pendingDiagDataForStop.filter((p) => !capturedJobsForStop.some((c) => c.jobId === p.jobId)),
  ];

  const handleStopConfirm = async () => {
    // oct20 — ONE rule for the button and the act (stopConfirmState): every
    // diagnostic/inspection job in my work is answered; "No" is accepted for
    // an inspection (FD-03) and for a diagnosis that continues (a colleague or
    // the next rollover day); a diagnosis this Stop ends needs its outcome
    // (CORE-AJ :46). The reason is shown in the dialog, never silent (D-3).
    const stopCaptures = effectiveStopCaptures();
    if (!stopConfirm.canConfirm) {
      toast.error(stopConfirm.blockers[0] ?? 'Answer the questions above to stop');
      return;
    }

    // Stage E: the vehicle's destination, when OptiBay needs it — never guessed.
    if ((stopNeedsDestination || stopDestination) && !stopDestinationFinal) {
      toast.error('Choose where you are leaving the vehicle');
      return;
    }

    // A projection from an earlier Pause/Resume describes a different moment:
    // the handoff metadata carries what THIS act learned, or null.
    lastTimerProjection.current = null;
    // Stop vs End Work follows THIS technician's live work state at confirm,
    // as the dialog's labels do: a refusal that refreshed the state (paused or
    // resumed on another device) re-targets the act instead of repeating it.
    const liveIntent = workState === 'paused' ? 'END_WORK' : 'STOP';
    if (interaction.current.intent !== liveIntent
      && !interaction.begin(liveIntent, planFor(liveIntent).steps)) return;
    const endingPausedWork = liveIntent === 'END_WORK';
    const captures = endingPausedWork ? [] : toCaptureAssertions(stopCaptures);
    const destination = stopDestinationFinal;
    // oct20a S1: what my session found, for each diagnosis this Stop does not end.
    const conclusions = stopConclusionJobs
      .filter((j) => sessionConclusionComplete(sessionConclusions[j.jobId]))
      .map((j) => sessionConclusionPayload(j.jobId, sessionConclusions[j.jobId]));

    interaction.complete('STOP_DETAILS');
    // STAGE 0A: only a COMMITTED boundary reaches the review and the handoff
    // follow-up — both are success effects, so a Stop that did not happen can
    // neither open a review nor be reported as handed to the front of house.
    // Stage E: ONE server act — end my work, and (only as the last live BOH
    // work) leave the vehicle where I said and hand the ticket to FOH.
    // Only answers for jobs that still need one travel; the server ignores
    // any job its own evidence already covers.
    // oct20 FD-02: plus every job left "not finished" — an inspection answered
    // "No" included — so the server leaves it open and does not complete the
    // ticket (it is handed to the front office instead).
    const dispositions = stopAnswers;
    const result = await submitTimerAct(
      endingPausedWork
        ? { intent: 'END_WORK', ticketId, destination, jobDispositions: dispositions, sessionConclusions: conclusions,
            entireRepair: stopScope === 'ENTIRE_REPAIR' }
        : { intent: 'STOP', ticketId, diagnosticCaptures: captures, destination, jobDispositions: dispositions, sessionConclusions: conclusions,
            entireRepair: stopScope === 'ENTIRE_REPAIR' },
      {
        ...commonEffects,
        REFRESH_BOARD: () => { closeStopDialog(); onTimerUpdate(); },
        FOH_HANDOFF: (r) => handOffAfterStop(r, endingPausedWork),
      },
    );
    if (!result) return;
    if (result.kind === 'refused' && result.sessionConclusionJobs.length > 0) {
      // oct20a S1: the server named diagnoses whose session conclusion it needs — asked here. Nothing changed.
      setServerConclusionJobs(result.sessionConclusionJobs);
    }
    if (result.kind === 'refused' && result.jobsNeedingDisposition.length > 0) {
      // Jobs with no frozen scope: ask for each, here. Nothing changed.
      setServerUnresolvedJobs(result.jobsNeedingDisposition);
      interaction.settle({ keepFacts: true });
      return;
    }
    if (result.kind === 'refused' && result.retryTarget) {
      // The server needs (another) destination: nothing changed. Ask again
      // from fresh occupancy; a refused spot is marked and never re-proposed.
      if (result.invalidatesTarget && destination?.kind === 'PARKING_SPOT') {
        setRefusedSpotIds((prev) => new Set(prev).add(destination.parkingSpotId));
        setStopDestination({ kind: 'PARKING_SPOT' });
      }
      setForceLocationQuestion(true);
      void readParkingSpotChoices(shopId, ticketId).then(setSpotChoices).catch(() => undefined);
      interaction.settle({ keepFacts: true });
      return;
    }
    if ((result.kind !== 'success' && captures.length > 0)
      || (result.kind === 'refused' && result.answerInDialog)
      || (result.kind === 'failed' && result.source === 'exception')) {
      // oct20: a diagnosis that needs its outcome keeps the dialog, so the
      // technician can record it right here (CORE-AJ :46) — not a dead end.
      // A refused diagnostic Stop saved nothing and stopped nothing, and a
      // request that threw may not have landed: keep the dialog and its inputs
      // so the technician can act. A definitive server answer closes it.
      interaction.settle({ keepFacts: true });
      return;
    }
    interaction.settle();
    if (result.kind !== 'success') closeStopDialog();
  };

  /** Cancel: work continues, the vehicle stays, custody is unchanged, and no choice survives. */
  const handleStopDialogClose = (open: boolean) => {
    if (open) { setIsStopDialogOpen(true); return; }
    if (interaction.busy) return;                         // a Stop in flight cannot be un-sent
    interaction.cancel();
    closeStopDialog();
  };

  /** Cancel: no pause, no request, no custody change, and no answer carried into the next attempt. */
  const handleDialogClose = (open: boolean) => {
    if (open) { setIsPauseDialogOpen(true); return; }
    if (interaction.busy) return;                         // a pause in flight cannot be un-sent
    interaction.cancel();
    closePauseDialog();
  };

  // Stage E: a required location must be complete; an optional one ("the
  // vehicle is somewhere else") must be complete if started.
  const stopLocationOk = stopNeedsDestination ? stopDestinationFinal !== null
    : (stopDestination === null || stopDestinationFinal !== null);
  // oct20 — when the Stop may be confirmed, and WHY NOT (shown in the dialog:
  // audit D-3). ALL jobs must be addressed (confirmed, fully answered, or an
  // explicit "No"); "No" is accepted per stopConfirmState.
  const stopCapturedIds = new Set(effectiveStopCaptures().map((c) => c.jobId));
  const stopCandidates = stopFindingsJobs.map((j) => ({ jobId: j.jobId, jobName: j.jobName, lockType: j.lockType }));
  const stopConclusionJobs = sessionConclusionJobs({
    ctx: finishCtx, serverJobs: serverConclusionJobs, capturedJobIds: stopCapturedIds,
    endingPausedWork: myState.myState === 'PAUSED',
  });
  const stopConfirm = stopConfirmState({
    ctx: finishCtx, ctxLoaded: finishCtxLoaded, locationOk: stopLocationOk,
    unresolvedJobs: unresolvedStopJobs, dispositions: jobDispositions,
    candidates: stopCandidates, capturedJobIds: stopCapturedIds, skippedJobIds: skippedJobIdsForStop,
    endingPausedWork: myState.myState === 'PAUSED',
    sessionConclusionJobs: stopConclusionJobs,
    concludedJobIds: new Set(stopConclusionJobs.filter((j) => sessionConclusionComplete(sessionConclusions[j.jobId])).map((j) => j.jobId)),
    stopScope,
  });
  const canConfirmStop = stopConfirm.canConfirm;
  const stopAnswers = stopJobDispositions({
    mayEndRepair: finishCtx === null || stopEnds, unresolvedJobs: unresolvedStopJobs, dispositions: jobDispositions,
    unfinishedJobIds, candidates: stopCandidates, skippedJobIds: skippedJobIdsForStop, capturedJobIds: stopCapturedIds,
  });
  // oct20 FD-02: the open jobs the final technician may leave "not finished"
  // (the ones not already asked about above).
  const stopScopedOpenJobs = stopEnds
    ? (finishCtx?.openJobs ?? []).filter((j) => !unresolvedStopJobs.some((u) => u.jobId === j.jobId)
        && !stopCandidates.some((c) => c.jobId === j.jobId))
    : [];
  const stopConsequenceOpts = {
    segmentContinues: finishCtx?.finalWorker === true && finishCtx.rolloverSegmentContinues === true,
    withheld: stopWithholdsCompletion(finishCtx, stopAnswers, stopScope,
      new Set(unresolvedStopJobs.filter((j) => j.kind === 'inspection').map((j) => j.jobId))),
    stopScope: stopChoiceOffered ? stopScope : undefined,
    withheldReasons: finishCtx?.completionBlockers ?? [],
    detailWithheld: finishCtx?.completionWithheld === true,
  };

  const renderPreAuthModal = () => (
    <PreAuthRequiredModal
      open={preAuthModal.open}
      onClose={() => setPreAuthModal((m) => ({ ...m, open: false }))}
      ticketId={ticketId}
      blocker={preAuthModal.blocker}
      canOverride={canOverridePreauth === true}
      onOverrideGranted={() => {
        // Retry the SAME act that was refused — same jobs, same bay.
        void retryAfterOverride();
      }}
    />
  );

  /**
   * Stage C — Guided Start: preview + countdown, or the bay choice. The same
   * component on every device; the commit is the ONE atomic move-and-start.
   */
  const renderGuidedStartDialog = () => (
    <div onClick={(e) => e.stopPropagation()}>
      <GuidedStartDialog
        open={guided !== null}
        mode={guided?.mode ?? 'choose'}
        decision={guided?.decision ?? null}
        choices={guided?.choices ?? []}
        vehicleLabel={guided?.vehicleLabel ?? null}
        layout={guided?.layout ?? null}
        busy={isPullingIn}
        act={guided?.act ?? 'start'}
        onCommit={(bayId) => { void commitGuidedStart(bayId); }}
        onCancel={cancelAct}
        onChooseDifferent={chooseDifferentBay}
        onStartHere={() => { void startHereDespitePlan(); }}
        onMoveFirst={moveFirstInstead}
      />
    </div>
  );

  /**
   * Stage D — "Why are you stopping work?": one answer, the same component on
   * every device. Diagnostic results for this technician's own diagnostic jobs
   * are an optional section inside it.
   */
  const renderPauseDialog = () => (
    <PauseWorkDialog
      open={isPauseDialogOpen}
      busy={isLoading}
      detail={pauseDetail}
      note={pauseNote}
      onDetailChange={setPauseDetail}
      onNoteChange={setPauseNote}
      onConfirm={() => { void handlePauseConfirm(); }}
      onCancel={() => handleDialogClose(false)}
      diagnosticSection={uncapturedDiagJobs.length > 0 ? (
        <DiagnosticPauseCapture
          uncapturedJobs={uncapturedDiagJobs}
          capturedJobs={capturedJobs}
          skippedJobIds={skippedJobIds}
          onJobCapture={handleJobCapture}
          onJobSkip={handleJobSkip}
          onPendingDataChange={setPendingDiagData}
        />
      ) : null}
    />
  );

  // Stop dialog body is identical from the running and paused branches —
  // a prior refactor left the running branch with an empty `...` placeholder,
  // which made the Stop button silently no-op while running. Render the
  // same dialog from both branches via this helper.
  /**
   * "What are you working on?" — the initial Start scope.
   *
   * Opens with NOTHING selected, and Start stays disabled until the technician
   * chooses. A preselected list would let passive acceptance become performer
   * evidence, and there is no implicit fallback to "all jobs" on cancel or
   * close: no selection means no timer, not a guess.
   *
   * Overlapping scope between technicians is allowed on purpose. Two people
   * can genuinely both work the water pump; assignment ownership and timer
   * participation are different facts.
   */
  const handleScopeDialogOpenChange = (open: boolean) => {
    setScopeDialogOpen(open);
    if (!open) {
      // Closing writes nothing. The selection is discarded rather than
      // remembered, so a later Start cannot inherit a half-made choice.
      setSelectedJobIds(new Set());
      setStartableJobs([]);
      interaction.cancel();
    }
  };

  const renderScopeDialog = () => (
    <Dialog
      open={scopeDialogOpen}
      onOpenChange={handleScopeDialogOpenChange}
    >
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>What are you working on?</DialogTitle>
          <DialogDescription>
            Choose the jobs this timer covers. Your time is attributed to what
            you select here.
          </DialogDescription>
          {continuedFrom && (
            <p className="text-sm text-muted-foreground" data-testid="scope-continued-work">
              Continuing repair work — jobs from {continuedFrom}.
            </p>
          )}
        </DialogHeader>

        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            data-testid="scope-select-all"
            disabled={isLoading}
            onClick={() => setSelectedJobIds(new Set(startableJobs.map((j) => j.jobId)))}
          >
            Select All
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="scope-deselect-all"
            disabled={isLoading}
            onClick={() => setSelectedJobIds(new Set())}
          >
            Deselect All
          </Button>
        </div>

        <div className="py-2 space-y-2" data-testid="scope-job-list">
          {startableJobs.map((job) => (
            <div key={job.jobId} className="flex items-start gap-2">
              <Checkbox
                id={`scope-job-${job.jobId}`}
                data-testid={`scope-job-${job.jobId}`}
                checked={selectedJobIds.has(job.jobId)}
                disabled={isLoading}
                onCheckedChange={(v) =>
                  setSelectedJobIds((prev) => {
                    const next = new Set(prev);
                    if (v === true) next.add(job.jobId);
                    else next.delete(job.jobId);
                    return next;
                  })
                }
              />
              <Label htmlFor={`scope-job-${job.jobId}`} className="cursor-pointer flex-1">
                {job.name}
                {job.expectedMinutes != null && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {job.expectedMinutes} min
                  </span>
                )}
              </Label>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            // Stage 0D: the controlled close does not fire onOpenChange, so
            // Cancel used to leave the remembered bay behind for the next Start.
            onClick={() => handleScopeDialogOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            data-testid="scope-start-confirm"
            onClick={() => void completeJobScope([...selectedJobIds])}
            disabled={selectedJobIds.size === 0 || isLoading}
          >
            {isLoading ? 'Starting...' : 'Start Timer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // 8E: one dialog instance, rendered by whichever branch is live. The
  // technician can reach it from the Stop that created the episode OR from the
  // persistent Review Time control after any interruption — same component,
  // same RPC, same authorization.
  const renderReviewDialog = () => (
    <TimerAllocationReviewDialog
      workEpisodeId={reviewEpisodeId}
      open={isReviewOpen}
      onOpenChange={setIsReviewOpen}
      onServerStateChanged={async () => {
        // Fires when this technician accepted an allocation AND when the reader
        // proved our cached state stale — a colleague or manager resolved the
        // episode from another device while this control still said
        // AWAITING_REVIEW.
        //
        // Re-read canonical state rather than assuming IDLE. Server truth wins:
        // on a ticket where a colleague is still working the ticket stays
        // `running`, and a technician with another unreviewed episode is still
        // AWAITING_REVIEW. Guessing here is how the 8B technician-vs-ticket
        // distinction gets quietly undone.
        await refreshMyState();
        onTimerUpdate();
      }}
    />
  );

  /**
   * Stage E — Simplified Stop: one dialog for Stop and End Paused Work, the
   * same on every device. Diagnostic close-out (running work only — End Work
   * has no open interval to attach findings to) is a section inside it.
   */
  const renderStopDialog = () => (
    <StopWorkDialog
      open={isStopDialogOpen}
      busy={isLoading}
      endingPausedWork={myState.myState === 'PAUSED'}
      mode={stopMode}
      whereNow={finishLocationWords(finishCtx?.location ?? null,
        (id) => spotChoices.find((s) => s.id === id)?.label ?? null)}
      forceLocationQuestion={forceLocationQuestion}
      destination={stopDestination}
      onDestinationChange={setStopDestination}
      cameraFoundSpotId={stopCamera.foundSpotId}
      cameraSuggestedSpotId={stopCamera.suggestedSpotId}
      spots={spotChoices}
      refusedSpotIds={refusedSpotIds}
      unresolvedJobs={unresolvedStopJobs}
      jobDispositions={jobDispositions}
      onJobDispositionChange={(jobId, d) => setJobDispositions((prev) => ({ ...prev, [jobId]: d }))}
      consequenceOptions={stopConsequenceOpts}
      openJobs={stopScopedOpenJobs}
      unfinishedJobIds={unfinishedJobIds}
      onToggleUnfinished={(jobId) => setUnfinishedJobIds((prev) => {
        const next = new Set(prev);
        if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
        return next;
      })}
      confirmBlockers={finishCtxLoaded ? stopConfirm.blockers : []}
      canConfirm={Boolean(canConfirmStop)}
      onConfirm={() => { void handleStopConfirm(); }}
      onCancel={() => handleStopDialogClose(false)}
      scopeChoice={stopChoiceOffered ? { value: stopScope, onChange: setStopScope } : null}
      diagnosticSection={stopFindingsJobs.length > 0 ? (
        <div className="py-2">
          <DiagnosticPauseCapture
            uncapturedJobs={stopFindingsJobs}
            capturedJobs={capturedJobsForStop}
            skippedJobIds={skippedJobIdsForStop}
            onJobCapture={handleJobCaptureForStop}
            onJobSkip={handleJobSkipForStop}
            onPendingDataChange={setPendingDiagDataForStop}
          />
        </div>
      ) : null}
      sessionConclusionSection={stopConclusionJobs.length > 0 ? (
        <SessionConclusionFields
          jobs={stopConclusionJobs}
          values={sessionConclusions}
          busy={isLoading}
          onChange={(jobId, next) => setSessionConclusions((prev) => ({ ...prev, [jobId]: next }))}
        />
      ) : null}
    />
  );

  // ═══════════════════════════════════════════════════════════════════════
  // CONTROL RENDERING — DRIVEN BY *MY* STATE, NOT THE TICKET'S
  // ═══════════════════════════════════════════════════════════════════════
  // `timerStatus` (the ticket projection) still belongs on the card; it just
  // no longer decides which buttons THIS technician receives. Two people on
  // one ticket legitimately need different controls at the same moment.
  const serviceInfoGate = (
    <ServiceInfoPrompt
      onComplete={handleServiceInfoComplete}
      onCancel={cancelAct}
    />
  );

  // ── 8E.1: ONE DIALOG INSTANCE, ONE STABLE POSITION ─────────────────────
  // The branch chain below returns a different control set per state. The
  // review dialog must NOT live inside it: when convergence flips
  // AWAITING_REVIEW -> IDLE, React would unmount the dialog from one branch
  // and mount a fresh one in another, which re-ran its load effect and fired
  // a second convergence notification. Rendering it once, outside the
  // switch, makes the instance survive its own convergence.
  // STAGE A: the controls come from ONE action model — this technician's
  // server-read work state and the acts it offers — not from scattered checks.
  const actions = availableTimerActions(workState);

  const renderBranch = () => {
    // A completed ticket offers only this technician's open time review.
    if (reviewOnly && workState !== 'awaiting_review') return null;

    // ── ACTIVE: this technician is measuring work right now ──────────────────
    if (workState === 'active') {
      return (
        <>
          <div className="flex gap-2" data-testid="ticket-timer-controls">
            {actions.includes('PAUSE') && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); handlePause(); }}
                disabled={isLoading}
                className="gap-2"
              >
                <Pause className="h-4 w-4" />
                Pause
              </Button>
            )}
            {actions.includes('STOP') && (
              <Button
                size="sm"
                variant="destructive"
                onClick={(e) => { e.stopPropagation(); handleStopClick(); }}
                disabled={isLoading}
                className="gap-2"
              >
                <Square className="h-4 w-4" />
                Stop Work
              </Button>
            )}
            {/* oct20a S1: findings any time during my diagnostic work (renders only when I may add some). */}
            <DiagnosticEvidenceButton ticketId={ticketId} refreshKey={workState} />
          </div>
          {renderPauseDialog()}
          {renderGuidedStartDialog()}
          {renderStopDialog()}
          {renderPreAuthModal()}
          </>
      );
    }

    // ── PAUSED: this technician's own episode is paused ──────────────────────
    // Note the second button is End Work, not Stop. There is no running timer of
    // theirs to stop, and the action routes to the End-Paused authority.
    if (workState === 'paused') {
      return (
        <>
          {showServiceInfoPrompt ? serviceInfoGate : (
            <div className="flex gap-2" data-testid="ticket-timer-controls">
              {actions.includes('RESUME') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); handleResumeClick(); }}
                  disabled={isLoading}
                  className="gap-2"
                >
                  <Play className="h-4 w-4" />
                  Resume
                </Button>
              )}
              {actions.includes('END_WORK') && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={(e) => { e.stopPropagation(); handleStopClick(); }}
                  disabled={isLoading}
                  className="gap-2"
                >
                  <Square className="h-4 w-4" />
                  End Work
                </Button>
              )}
              <DiagnosticEvidenceButton ticketId={ticketId} refreshKey={workState} />
            </div>
          )}
          {renderStopDialog()}
          {/* Oct 20 (D-4): Resume of parked paused work asks which bay — the same chooser as Guided Start. */}
          {renderGuidedStartDialog()}
          {renderPreAuthModal()}
          </>
      );
    }

    // ── AWAITING_REVIEW: stopped work whose time is not yet allocated ────────
    // No Start, no Resume, no Pause — the server would refuse all three until
    // this episode's time is allocated.
    //
    // 8E made this actionable. It used to render "Time review required" and
    // nothing else, which was a dead end: the only escape was the dialog that
    // happened to be on screen when Stop returned, so closing it, refreshing, or
    // coming back tomorrow left the technician stuck with a blocked Start and no
    // way forward. The episode id comes from `my_timer_control_state`, so this
    // button is a genuine recovery path rather than a survivor of local state.
    if (workState === 'awaiting_review' && actions.includes('REVIEW_TIME')) {
      return (
        <>
          <div className="flex items-center gap-2" data-testid="ticket-timer-controls">
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              data-testid="timer-review-required"
              onClick={(e) => { e.stopPropagation(); openReview(myState.workEpisodeId); }}
            >
              <Square className="h-3.5 w-3.5" />
              Review Time
            </Button>
          </div>
          </>
      );
    }

    // ── NOT_TECHNICIAN: managers and advisors get no technician timer ────────
    if (workState === 'not_technician') {
      return null;
    }

    // ── INTEGRITY_ERROR / UNKNOWN: offer nothing consequential ───────────────
    // Guessing here is how a technician ends up starting a second timer or
    // stopping work that is not theirs. Saying nothing is the safe answer, and
    // the condition is logged rather than smoothed over.
    if (workState === 'unavailable' || workState === 'checking') {
      if (workState === 'unavailable') {
        console.error(
          '[TicketTimerControls] contradictory canonical timer state for this technician',
          { ticketId },
        );
      }
      return (
        <div
          className="flex items-center gap-2 text-xs text-muted-foreground"
          data-testid="ticket-timer-controls"
        >
          <span data-testid="timer-state-unavailable">
            {workState === 'unavailable'
              ? 'Timer state needs attention'
              : 'Checking timer…'}
          </span>
        </div>
      );
    }

    // ── IDLE: this technician is not working this ticket ─────────────────────
    // Reached whether the TICKET is stopped, running for a colleague, or paused
    // for a colleague. In every one of those cases the truthful control for this
    // person is the same: start your own work.
    //
    // FILM MODE deliberately does not appear in any branch above. Marketing mode
    // may change presentation, but it must never fabricate ACTIVE for a
    // technician who is not working: the server would refuse the action anyway,
    // and a demo that produces real timer evidence is not a demo.
    //
    // OCT28 #5: the act is the same START, but when the TICKET's authoritative projection says a colleague's timer is
    // running (or paused) here, the button says so — "Join Work" — instead of a generic "Start Timer" that reads as if
    // nobody were on the car.
    const colleagueTimer = myState.ticketTimerStatus ?? timerStatus;
    const colleagueWorking = colleagueTimer === 'running' || colleagueTimer === 'paused';
    return (
      <>
        {showServiceInfoPrompt ? serviceInfoGate : (
          <div data-testid="ticket-timer-controls">
            {actions.includes('START') && (
              <div className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="timer-start-button"
                  onClick={(e) => { e.stopPropagation(); handleStartClick(); }}
                  disabled={isLoading}
                  className="gap-2"
                >
                  <Play className="h-4 w-4" />
                  {colleagueWorking ? 'Join Work' : 'Start Timer'}
                </Button>
                {colleagueWorking && (
                  <span className="text-xs text-muted-foreground" data-testid="timer-colleague-state">
                    {colleagueTimer === 'running'
                      ? 'Another technician is working this ticket'
                      : 'Paused by another technician'}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        {renderScopeDialog()}
        {renderPauseDialog()}
        {renderGuidedStartDialog()}
        {renderPreAuthModal()}
        {/* 8E.1: the review dialog must survive its own convergence. When the
            reader reports ALREADY_RESOLVED the parent re-reads and lands HERE
            (IDLE) — if this branch did not mount the dialog, the "already
            reviewed" message would be yanked off screen by the very refresh it
            triggered. The technician closes it deliberately instead. */}
      </>
    );
  };

  return (
    <>
      {renderBranch()}
      {renderReviewDialog()}
    </>
  );
}